import React, { useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import {
  ShareNetwork,
  Copy,
  WhatsappLogo,
  LinkSimpleBreak,
  CheckCircle,
  X,
} from "@phosphor-icons/react";

/**
 * Modal para generar / revocar / compartir el link público de una rutina.
 * Extraído de RoutinesTab.jsx — 100% self-contained, no toca estado del parent
 * más que vía `onChanged` (para refrescar la lista) y `onClose`.
 */
export default function ShareModal({ routine, onClose, onChanged }) {
  const [sharing, setSharing] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [token, setToken] = useState(routine.share_token || null);
  const [copied, setCopied] = useState(false);

  const publicUrl = token ? `${window.location.origin}/r/${token}` : "";

  const handleShare = async () => {
    setSharing(true);
    try {
      const { data } = await api.post(`/routines/${routine.routine_id}/share`);
      setToken(data.share_token);
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSharing(false);
    }
  };

  const handleRevoke = async () => {
    if (!window.confirm("¿Revocar el link público? Quien tenga el link ya no podrá verlo.")) return;
    setRevoking(true);
    try {
      await api.delete(`/routines/${routine.routine_id}/share`);
      setToken(null);
      onChanged?.();
      toast.success("Link revocado");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setRevoking(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("No se pudo copiar el link");
    }
  };

  const whatsappUrl = () => {
    const text = `Te comparto la rutina "${routine.name}" en Athlon Sports: ${publicUrl}`;
    return `https://wa.me/?text=${encodeURIComponent(text)}`;
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" data-testid="share-modal">
      <div className="card-base w-full max-w-lg p-6">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <ShareNetwork size={22} weight="duotone" className="text-primary-app" />
            <h3 className="font-display font-black uppercase text-2xl tracking-tighter">Compartir rutina</h3>
          </div>
          <button onClick={onClose} className="text-muted hover:text-white" data-testid="share-close"><X size={22} weight="bold" /></button>
        </div>

        <div className="text-xs uppercase tracking-widest text-muted mb-1">Rutina</div>
        <div className="font-display font-bold uppercase text-lg mb-5">{routine.name}</div>

        {!token ? (
          <>
            <p className="text-sm text-muted mb-5">
              Generá un link público para que cualquiera pueda ver esta rutina sin necesidad de cuenta. Podrás revocar el link en cualquier momento.
            </p>
            <button
              onClick={handleShare}
              disabled={sharing}
              className="btn-primary w-full flex items-center justify-center gap-2"
              data-testid="share-generate-btn"
            >
              <ShareNetwork size={16} weight="bold" />
              {sharing ? "Generando..." : "Generar link público"}
            </button>
          </>
        ) : (
          <>
            <div className="text-xs uppercase tracking-widest text-muted mb-2">Link público</div>
            <div className="flex gap-2 mb-4">
              <input
                readOnly
                value={publicUrl}
                onClick={(e) => e.target.select()}
                className="input-base flex-1 !py-2 text-xs"
                data-testid="share-url-input"
              />
              <button
                onClick={handleCopy}
                className={`btn-secondary !py-2 !px-3 flex items-center gap-1 ${copied ? "!border-[#32D74B] text-[#32D74B]" : ""}`}
                data-testid="share-copy-btn"
              >
                {copied ? <CheckCircle size={16} weight="bold" /> : <Copy size={16} weight="bold" />}
                {copied ? "Copiado" : "Copiar"}
              </button>
            </div>

            <div className="flex gap-2 flex-wrap mb-5">
              <a
                href={whatsappUrl()}
                target="_blank"
                rel="noreferrer"
                className="btn-secondary flex items-center gap-2 !border-[#25D366] text-[#25D366] hover:!border-white hover:text-white"
                data-testid="share-whatsapp-btn"
              >
                <WhatsappLogo size={16} weight="bold" /> WhatsApp
              </a>
              <a
                href={publicUrl}
                target="_blank"
                rel="noreferrer"
                className="btn-ghost flex items-center gap-2"
                data-testid="share-preview-btn"
              >
                Ver preview
              </a>
            </div>

            <div className="border-t border-app pt-4">
              <button
                onClick={handleRevoke}
                disabled={revoking}
                className="text-xs uppercase tracking-widest text-muted hover:text-[#FF3B30] flex items-center gap-2 transition-colors"
                data-testid="share-revoke-btn"
              >
                <LinkSimpleBreak size={14} weight="bold" />
                {revoking ? "Revocando..." : "Revocar link público"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
